/**
 * AppleSignInButton — botón "Continuar con Apple" para /login y /register.
 *
 * Usa la librería `react-apple-signin-auth` que envuelve el JS SDK oficial
 * de Apple (cargado dinámicamente). Flow popup (`usePopup: true`) → al
 * éxito, recibimos `authorization.id_token` que mandamos al backend para
 * que lo verifique contra Apple JWKS.
 *
 * Comportamiento:
 * - Si VITE_APPLE_CLIENT_ID NO está configurado, renderiza null
 *   (el resto del auth sigue funcionando sin Apple).
 * - Si está configurado, renderiza el botón oficial de Apple con tema
 *   dark (encaja con paleta dark Nexum y cumple guideline de Apple).
 * - Al éxito de Apple: invoca `onIdToken(id_token, user?)`. El campo
 *   `user` (firstName + lastName) SOLO viene en el primer login del
 *   usuario; el backend lo usa para popular `User.full_name` si está
 *   vacío.
 * - Al error de Apple: invoca `onError(code)` con código i18n traducible.
 *
 * Apple cumplimiento: Apple exige que su botón sea igual o más prominente
 * que cualquier otro método social. Por eso este componente se renderiza
 * ARRIBA del GoogleSignInButton en /login y /register.
 *
 * Cobertura: ver src/__tests__/components/auth/AppleSignInButton.test.tsx
 * y src/__tests__/flows/apple-oauth-flow.integration.test.tsx.
 */
import AppleSigninButton from "react-apple-signin-auth";

import { t } from "@/i18n/es";

interface AppleAuthResponse {
  authorization: {
    id_token: string;
    code: string;
    state?: string;
  };
  /** Apple SOLO entrega `user` en el primer login. Después es undefined. */
  user?: {
    email: string;
    name: {
      firstName: string;
      lastName: string;
    };
  };
}

interface Props {
  onIdToken: (
    idToken: string,
    name?: { firstName: string; lastName: string },
  ) => void;
  onError: (code: string) => void;
}

const HAS_APPLE_CLIENT_ID = Boolean(import.meta.env.VITE_APPLE_CLIENT_ID);
const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID ?? "";

// redirectURI debe coincidir con uno registrado en el Service ID de Apple
// (ver docs/operations/setup-apple-developer.md). Usamos el de producción
// porque Apple NO acepta localhost. En desarrollo, el popup fallará si el
// dominio del frontend no está autorizado — DEUDA TÉCNICA Sprint 7.
const APPLE_REDIRECT_URI = "https://api.nexumrun.com/auth/oauth/apple/callback";

export function AppleSignInButton({ onIdToken, onError }: Props) {
  if (!HAS_APPLE_CLIENT_ID) {
    // Sin client_id en .env, no renderizamos nada. La página sigue funcional
    // con email/password y Google.
    return null;
  }

  // El cast a `unknown` y luego al tipo es necesario porque la librería
  // tipa `onSuccess` como `Function` (lowercase) en su .d.ts; mantenemos
  // el handler tipado fuerte localmente.
  function handleSuccess(response: unknown) {
    const apple = response as AppleAuthResponse;
    const idToken = apple?.authorization?.id_token;
    if (!idToken) {
      onError("id_token_required");
      return;
    }
    onIdToken(idToken, apple.user?.name);
  }

  function handleError(error: unknown) {
    // El SDK de Apple lanza errores con shape { error: 'popup_closed_by_user', ... }
    // Distinguimos el cierre del popup (no es un error real) del resto.
    const code = (error as { error?: string })?.error;
    if (code === "popup_closed_by_user") {
      // Usuario cerró el popup: silencio. No mostrar error.
      return;
    }
    onError("apple_token_invalid");
  }

  return (
    <div className="flex flex-col gap-3" data-testid="apple-signin-section">
      <div className="flex justify-center">
        <AppleSigninButton
          authOptions={{
            clientId: APPLE_CLIENT_ID,
            scope: "email name",
            redirectURI: APPLE_REDIRECT_URI,
            usePopup: true,
          }}
          uiType="dark"
          // El botón de Apple no acepta width directo; controlamos con className.
          // w-full max-w-xs (responsive): antes era w-80 (320px fijo) que
          // desbordaba en viewports estrechos.
          className="w-full max-w-xs"
          onSuccess={handleSuccess}
          onError={handleError}
          // Texto custom: Apple permite cambiarlo siempre que mantenga el
          // formato "X with Apple" / "Continuar con Apple" (guideline).
          buttonExtraChildren={t.auth.appleCta}
        />
      </div>
    </div>
  );
}
